/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState } from 'react';
import { FileText, RefreshCw, Wifi, Check, X, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import type { SharePointOrgSettings } from '@shared/types/organizationSettings';

interface SharePointConnectionStatus {
  tested: boolean;
  success: boolean;
  error?: string;
  details?: {
    siteName?: string;
    siteUrl?: string;
    documentLibraries?: number;
  };
}

interface SharePointSectionProps {
  settings: SharePointOrgSettings;
  onChange: (settings: SharePointOrgSettings) => void;
  onDirty: () => void;
}

export function SharePointSection({
  settings,
  onChange,
  onDirty
}: SharePointSectionProps) {
  const { toast } = useToast();
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<SharePointConnectionStatus>({
    tested: false,
    success: false
  });
  const [showClientSecret, setShowClientSecret] = useState(false);

  const testConnection = async () => {
    if (!settings.tenantId) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: 'Please provide an Azure AD Tenant ID'
      });
      return;
    }

    if (!settings.clientId) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: 'Please provide an Azure AD Client ID'
      });
      return;
    }

    if (!settings.clientSecret) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: 'Please provide an Azure AD Client Secret'
      });
      return;
    }

    if (!settings.siteUrl) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: 'Please provide a SharePoint Site URL'
      });
      return;
    }

    setIsTestingConnection(true);
    setConnectionStatus({ tested: false, success: false });

    try {
      const response = await fetch('/api/settings/sharepoint/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: settings.tenantId,
          clientId: settings.clientId,
          clientSecret: settings.clientSecret,
          siteUrl: settings.siteUrl,
        })
      });

      const result = await response.json();

      if (result.success && result.data?.success) {
        setConnectionStatus({
          tested: true,
          success: true,
          details: result.data.details
        });
        toast({
          title: 'Connection successful',
          description: result.data.message || 'Successfully connected to SharePoint',
        });
      } else {
        setConnectionStatus({
          tested: true,
          success: false,
          error: result.data?.message || result.error?.message || 'Connection failed'
        });
      }
    } catch (error: any) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: error.message || 'Connection test failed'
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  return (
    <div style={{
      marginBottom: '32px',
      padding: '20px',
      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      borderRadius: '8px',
      border: '1px solid #93c5fd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <FileText size={20} style={{ marginRight: '8px', color: '#2563eb' }} />
        <h3 style={{ margin: 0, color: '#1e3a5f' }}>Microsoft SharePoint Integration</h3>
      </div>

      <p style={{ fontSize: '13px', color: '#1e40af', marginBottom: '16px' }}>
        Configure SharePoint as a data source for accessing documents and resources.
        Uses Microsoft Graph API with OAuth2 client credentials for secure, centralized access
        to SharePoint sites across your organization.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => {
              onChange({ ...settings, enabled: e.target.checked });
              onDirty();
            }}
          />
          <span style={{ fontSize: '14px', color: '#1e3a5f' }}>Enable SharePoint integration</span>
        </label>
      </div>

      {settings.enabled && (
        <div style={{
          padding: '16px',
          background: '#fff',
          borderRadius: '6px',
          border: '1px solid #bfdbfe'
        }}>
          <div className="form-group">
            <label htmlFor="sp-tenant-id">Azure AD Tenant ID <span style={{ color: '#dc2626' }}>*</span></label>
            <input
              id="sp-tenant-id"
              type="text"
              placeholder="e.g., 12345678-abcd-1234-efgh-123456789012"
              value={settings.tenantId}
              onChange={(e) => {
                onChange({ ...settings, tenantId: e.target.value });
                onDirty();
                setConnectionStatus({ tested: false, success: false });
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Your Azure Active Directory tenant (directory) ID. Found in Azure Portal → Azure Active Directory → Overview.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="sp-client-id">Application (Client) ID <span style={{ color: '#dc2626' }}>*</span></label>
            <input
              id="sp-client-id"
              type="text"
              placeholder="e.g., abcdef12-3456-7890-abcd-ef1234567890"
              value={settings.clientId}
              onChange={(e) => {
                onChange({ ...settings, clientId: e.target.value });
                onDirty();
                setConnectionStatus({ tested: false, success: false });
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              The application (client) ID from your Azure AD app registration.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="sp-client-secret">Client Secret <span style={{ color: '#dc2626' }}>*</span></label>
            <div style={{ position: 'relative' }}>
              <input
                id="sp-client-secret"
                type={showClientSecret ? 'text' : 'password'}
                placeholder="Enter your client secret"
                value={settings.clientSecret}
                onChange={(e) => {
                  onChange({ ...settings, clientSecret: e.target.value });
                  onDirty();
                  setConnectionStatus({ tested: false, success: false });
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  paddingRight: '40px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <button
                type="button"
                onClick={() => setShowClientSecret(!showClientSecret)}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                }}
                title={showClientSecret ? 'Hide secret' : 'Show secret'}
              >
                {showClientSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              A client secret from your Azure AD app registration. Found in Azure Portal → App Registrations → Certificates & Secrets.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="sp-site-url">SharePoint Site URL <span style={{ color: '#dc2626' }}>*</span></label>
            <input
              id="sp-site-url"
              type="url"
              placeholder="https://contoso.sharepoint.com/sites/engineering"
              value={settings.siteUrl}
              onChange={(e) => {
                onChange({ ...settings, siteUrl: e.target.value });
                onDirty();
                setConnectionStatus({ tested: false, success: false });
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              The full URL of the SharePoint site to connect to.
            </small>
          </div>

          <div style={{
            marginTop: '12px',
            padding: '12px',
            background: '#eff6ff',
            borderRadius: '4px',
            border: '1px solid #bfdbfe',
            fontSize: '13px',
            color: '#1e40af'
          }}>
            <strong>Azure AD App Registration Setup:</strong>
            <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Register an application in Azure Portal → Azure Active Directory → App Registrations</li>
              <li>Add API permission: Microsoft Graph → Application → <code style={{ background: '#dbeafe', padding: '1px 4px', borderRadius: '2px' }}>Sites.Read.All</code></li>
              <li>Grant admin consent for the permissions</li>
              <li>Create a client secret under Certificates & Secrets</li>
            </ol>
          </div>

          {/* Test Connection Button */}
          <div style={{ marginTop: '16px' }}>
            <button
              className="btn"
              onClick={testConnection}
              disabled={isTestingConnection || !settings.tenantId || !settings.clientId || !settings.clientSecret || !settings.siteUrl}
              style={{
                width: 'auto',
                padding: '8px 16px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isTestingConnection ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  Testing Connection...
                </>
              ) : (
                <>
                  <Wifi size={16} />
                  Test Connection
                </>
              )}
            </button>

            {connectionStatus.tested && (
              <div style={{ marginTop: '12px' }}>
                {connectionStatus.success ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <Check size={16} style={{ color: '#28a745', marginRight: '8px' }} />
                      <span style={{
                        padding: '4px 12px',
                        background: '#d1fae5',
                        color: '#065f46',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: 500
                      }}>
                        Connected to SharePoint
                      </span>
                    </div>
                    {connectionStatus.details && (
                      <div style={{
                        padding: '8px 12px',
                        background: '#f0fdf4',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#166534'
                      }}>
                        {connectionStatus.details.siteName && (
                          <div><strong>Site Name:</strong> {connectionStatus.details.siteName}</div>
                        )}
                        {connectionStatus.details.siteUrl && (
                          <div><strong>Site URL:</strong> {connectionStatus.details.siteUrl}</div>
                        )}
                        {connectionStatus.details.documentLibraries !== undefined && (
                          <div><strong>Document Libraries:</strong> {connectionStatus.details.documentLibraries}</div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <X size={16} style={{ color: '#dc3545', marginRight: '8px' }} />
                    <span style={{
                      padding: '4px 12px',
                      background: '#fef2f2',
                      color: '#b91c1c',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontWeight: 500
                    }}>
                      {connectionStatus.error || 'Connection failed'}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
